import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

type SimpleNotificationType =
  | 'SimpleNotificationDanger'
  | 'SimpleNotificationInformation'
  | 'SimpleNotificationSuccess'
  | 'SimpleNotificationWarning'

type SimpleNotificationLinkAction = () => Promise<void> | void

// A clickable link rendered inside the notification message. The `label` must be an
// exact substring of the message text; clicking it runs `action`.
interface SimpleNotificationLink {
  action: SimpleNotificationLinkAction
  label: string
}

interface SimpleNotification {
  key: number
  message: string
  type: SimpleNotificationType
  dismissable?: boolean
  links?: SimpleNotificationLink[]
}
const defaultDisplayDurationInMilliseconds = 3000

const useSimpleNotificationStore = defineStore('SimpleNotificationStore', () => {
  const displayDurationInMilliseconds = ref(defaultDisplayDurationInMilliseconds)
  const messages = reactive<SimpleNotification[]>([])

  const close = (key: number): void => {
    const idx = messages.findIndex((message) => message.key === key)
    if (idx !== -1) messages.splice(idx, 1)
  }

  const clearAll = (): void => {
    messages.splice(0, messages.length)
  }

  const triggerSimpleNotificationMessage = (
    messageType: SimpleNotificationType,
    messageText?: string,
    dismissable = false,
    links?: SimpleNotificationLink[],
  ): number => {
    const key = Date.now()
    messages.push({
      dismissable,
      key,
      message: messageText ?? '',
      type: messageType,
      ...(links ? { links } : {}),
    })
    if (!dismissable) {
      const timer = setTimeout(() => {
        close(key)
        clearTimeout(timer)
      }, displayDurationInMilliseconds.value)
    }

    return key
  }

  const triggerSimpleNotificationError = (
    messageText?: string,
    isWarning = false,
    dismissable = false,
  ): void => {
    const currentMessageType: SimpleNotificationType = isWarning
      ? 'SimpleNotificationWarning'
      : 'SimpleNotificationDanger'

    triggerSimpleNotificationMessage(currentMessageType, messageText, dismissable)
  }
  const triggerSimpleNotificationInformation = (
    messageText?: string,
    dismissable?: boolean,
    links?: SimpleNotificationLink[],
  ): number => {
    return triggerSimpleNotificationMessage(
      'SimpleNotificationInformation',
      messageText,
      dismissable,
      links,
    )
  }
  const triggerSimpleNotificationSuccess = (messageText?: string, dismissable = false): void => {
    triggerSimpleNotificationMessage('SimpleNotificationSuccess', messageText, dismissable)
  }

  return {
    clearAll,
    close,
    displayDurationInMilliseconds,
    messages,
    triggerSimpleNotificationError,
    triggerSimpleNotificationInformation,
    triggerSimpleNotificationSuccess,
  }
})

export { useSimpleNotificationStore }
export type { SimpleNotificationLink, SimpleNotificationLinkAction }
